import { getVariantConfig } from '../../../../../shared-generation-pricing-config'
import { ModelDetailSection } from './ModelDetailSection'
import type { GenerationType, PriceUnit } from '../../../../../shared-generation-pricing-config'
import {
  formatCost,
  formatPriceUnit,
  humanizeLabel,
  humanizeTierValue,
  isDurationTieredPricing,
  isTextLengthTieredPricing,
} from '@/shared/utils/generationPricing'
import { getParamMeta } from '@/features/models/constants/paramMeta'

interface ModelDetailGenerationPricingProps {
  baseModel: string
  genType: GenerationType
  priceUnit: PriceUnit
}

export function ModelDetailGenerationPricing({ baseModel, genType, priceUnit }: ModelDetailGenerationPricingProps) {
  const variant = getVariantConfig(baseModel, genType)
  if (!variant) return null

  const p = variant.pricing
  const unit = variant.priceUnit ?? priceUnit

  return (
    <ModelDetailSection title="Pricing" note={formatPriceUnit(unit)}>
      <div className="surface-bevel overflow-hidden rounded-[1.5rem] px-5">
        {typeof p === 'number' && <FlatRow price={p} unit={unit} last />}

        {isTextLengthTieredPricing(p) && (
          <FlatRow price={p.ratePer1kChars} unit="per-1k-characters" last />
        )}

        {isDurationTieredPricing(p) && (
          <>
            {Object.entries(p.ratesPerSecond).map(([key, rate], i, arr) => {
              const values = key.split('|')
              const label = p.tierParams.map((param, idx) => humanizeTierValue(param, values[idx] ?? '')).join(' · ')
              return (
                <TierRow key={key} label={label || 'Default'} price={rate} unit={unit} last={i === arr.length - 1} />
              )
            })}
            <p className="py-2 text-xs text-[var(--color-text-tertiary)]">
              Default duration: {p.defaultDurationSeconds}s
            </p>
          </>
        )}

        {typeof p === 'object' && !('kind' in p) && (
          <>
            {Object.entries(p).map(([paramName, tiers]) => {
              const entries = Object.entries(tiers)
              return entries.map(([value, price], i) => (
                <TierRow
                  key={`${paramName}-${value}`}
                  label={`${getParamMeta(paramName).label}: ${humanizeLabel(value)}`}
                  price={price}
                  unit={unit}
                  last={i === entries.length - 1}
                />
              ))
            })}
          </>
        )}
      </div>
    </ModelDetailSection>
  )
}

function FlatRow({ price, unit, last }: { price: number; unit: PriceUnit; last?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-3 text-sm ${last ? '' : 'border-b border-[var(--color-border)]'}`}>
      <span className="text-[var(--color-text-secondary)]">Price</span>
      <span className="font-mono font-semibold tabular-nums text-[var(--color-text-primary)]">
        {formatCost(price)} {formatPriceUnit(unit)}
      </span>
    </div>
  )
}

function TierRow({ label, price, unit, last }: { label: string; price: number; unit: PriceUnit; last?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-3 text-sm ${last ? '' : 'border-b border-[var(--color-border)]'}`}>
      <span className="text-[var(--color-text-secondary)]">{label}</span>
      <span className="font-mono font-semibold tabular-nums text-[var(--color-text-primary)]">
        {formatCost(price)} {formatPriceUnit(unit)}
      </span>
    </div>
  )
}
